import React from "react";
import { FormattedMessage } from "react-intl";
import Skeleton from "react-loading-skeleton";

import { type TExpert, useGetMeetingsExpertsQuery } from "~/api";
import { formatPersonName } from "~/components/common/PersonName";
import { Button } from "~/components/ui/button";

import { EmptyDisplay, ExpertImage } from "./meetings-shared";

export function ExpertsList({ onExpertClick }: { onExpertClick(expert: TExpert): void }) {
  const expertsQuery = useGetMeetingsExpertsQuery();

  if (expertsQuery.isUninitialized || expertsQuery.isLoading) {
    return (
      <ul role="list" className="tw-flex tw-list-none tw-flex-col tw-gap-4 tw-px-0">
        {Array.from({ length: 3 }, (_, index) => (
          <Expert
            key={index}
            expert={undefined}
            onExpertClick={onExpertClick}
            displayHr={index < 5}
          />
        ))}
      </ul>
    );
  }

  if (expertsQuery.isError) {
    return (
      <EmptyDisplay
        title={<FormattedMessage id="meetings.error" />}
        description={<FormattedMessage id="events.error.description" />}
        isError
      />
    );
  }

  if (expertsQuery.data.length == 0) {
    return (
      <EmptyDisplay
        title={<FormattedMessage id="meetings.not.found" />}
        description={<FormattedMessage id="meetings.not.found.description" />}
        link={{ to: "#", children: "Alignment", className: "tw-invisible" }}
      />
    );
  }

  return (
    <ul role="list" className="tw-flex tw-list-none tw-flex-col tw-gap-4 tw-p-0">
      {expertsQuery.data.map((expert, index, array) => (
        <Expert
          key={expert.account_id}
          expert={expert}
          onExpertClick={onExpertClick}
          displayHr={index < array.length - 1}
        />
      ))}
    </ul>
  );
}

function Expert({
  expert,
  onExpertClick,
  displayHr,
}: {
  expert: TExpert | undefined;
  onExpertClick(expert: TExpert): void;
  displayHr: boolean;
}) {
  const description = expert ? getDescription(expert.account_id) : "";

  const imageElement = <ExpertImage expert={expert} />;

  const nameElement = expert ? (
    <span>
      {formatPersonName({ person: expert, useMiddleName: true })}
      {description ? (
        <span className="tw-block tw-text-xs tw-text-gray-400">{description}</span>
      ) : null}
    </span>
  ) : (
    <Skeleton
      className="tw-rounded-md"
      containerClassName="tw-space-y-1.5"
      count={2}
      width={200}
      height={15}
    />
  );

  const buttonElement = expert ? (
    <Button rounded className="tw-uppercase" onClick={() => onExpertClick(expert)}>
      <FormattedMessage id="meetings.book" />
    </Button>
  ) : (
    <Skeleton className="tw-rounded-md" width={120} height={36} style={{ lineHeight: "20px" }} />
  );

  return (
    <li>
      <div className="tw-flex tw-items-center tw-gap-8 sm:tw-hidden">
        {imageElement}

        <div className="tw-flex tw-flex-col tw-gap-4">
          {nameElement}

          <div className="tw-grow-0">{buttonElement}</div>
        </div>
      </div>

      <div className="tw-hidden tw-items-center tw-justify-between sm:tw-flex">
        <div className="tw-flex tw-items-center tw-gap-3">
          {imageElement}
          {nameElement}
        </div>

        {buttonElement}
      </div>

      {displayHr ? <hr className="tw-mb-0 tw-mt-4" /> : null}
    </li>
  );
}

function getDescription(expertId: number): string {
  const MOLOTOVA_ACCOUNT_ID = 3320;
  const SOLOP_ACCOUNT_ID = 3342;

  switch (expertId) {
    case MOLOTOVA_ACCOUNT_ID:
      return "ДЕТИ";
    case SOLOP_ACCOUNT_ID:
      return "ДВНЧС";
    default:
      return "";
  }
}
